import React from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle, CloudUpload, Download } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

/**
 * First step of every spreadsheet upload in admissions (applicants, marks):
 * 1 · download a template made for this cycle, 2 · drop the filled sheet
 * back, plus a short "good to know" list.
 */

interface Props {
  /** What the template contains, in a sentence or two. */
  templateText: React.ReactNode;
  templateButtonLabel: string;
  buildingTemplate: boolean;
  onDownloadTemplate: () => void;
  /** Nothing to put in the template yet — `templateText` says why. */
  templateDisabled?: boolean;
  onFile: (file: File) => void;
  /** Reading / checking the uploaded file. */
  busy: boolean;
  busyText: string;
  /** Under the drop zone, e.g. ".xlsx · up to 500 applicants". */
  capText: string;
  tips: string[];
}

const StepBadge: React.FC<{ n: number }> = ({ n }) => (
  <Box sx={{
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
    bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: 'primary.main',
  }}>
    {n}
  </Box>
);

const BulkSheetPickStage: React.FC<Props> = ({
  templateText, templateButtonLabel, buildingTemplate, onDownloadTemplate, templateDisabled, onFile, busy, busyText, capText, tips,
}) => {
  const theme = useTheme();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => { if (accepted[0]) onFile(accepted[0]); },
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    multiple: false,
    disabled: busy,
  });

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, display: 'flex', flexDirection: 'column' }}>
          <StepBadge n={1} />
          <Typography fontWeight={700} sx={{ mt: 1.5 }}>Download the template</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, flex: 1 }}>{templateText}</Typography>
          <Button
            variant="outlined"
            startIcon={buildingTemplate ? <CircularProgress size={16} /> : <Download />}
            onClick={onDownloadTemplate}
            disabled={buildingTemplate || templateDisabled}
            sx={{ mt: 2.5, alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {buildingTemplate ? 'Preparing…' : templateButtonLabel}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5, display: 'flex', flexDirection: 'column' }}>
          <StepBadge n={2} />
          <Typography fontWeight={700} sx={{ mt: 1.5 }}>Upload the filled sheet</Typography>
          <Box
            {...getRootProps()}
            sx={{
              mt: 1.5, flex: 1, minHeight: 132, p: 2, borderRadius: 2,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              cursor: busy ? 'progress' : 'pointer',
              border: `1.5px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.text.primary, 0.02),
              transition: 'all 120ms ease',
              '&:hover': busy ? undefined : { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
            }}
          >
            <input {...getInputProps()} />
            {busy ? (
              <>
                <CircularProgress size={24} sx={{ mb: 1.25 }} />
                <Typography variant="body2" color="text.secondary">{busyText}</Typography>
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 30, color: isDragActive ? 'primary.main' : 'text.secondary', mb: 1 }} />
                <Typography variant="body2" fontWeight={600}>
                  {isDragActive ? 'Drop to upload' : <>Drop your file here or <Box component="span" sx={{ color: 'primary.main' }}>browse</Box></>}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>{capText}</Typography>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <Box>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Good to know
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {tips.map(t => (
            <Stack key={t} direction="row" spacing={1} alignItems="flex-start">
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: 0.3 }} />
              <Typography variant="body2" color="text.secondary">{t}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default BulkSheetPickStage;
